import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  InputProps,
  Pagination,
  RadioGroup,
  SpaceBetween,
  Table,
  TextFilter,
  TextFilterProps,
} from "@amzn/awsui-components-react";
import {
  OmsPresetMetadata,
  OmsPresetScope,
} from "../../lusca-search-api/generated-src";
import { OmsPresetsState, presetErrorMessage } from "../../hooks/useOmsPresets";
import { PresetRetryExpiredError } from "../../hooks/omsPresetRetry";
import { reportTimestamp } from "./OMSReportSchedule";
import { presetScopeLabel } from "./omsPresetEditing";

export function OMSPresetSave({
  name,
  scope,
  onName,
  onScope,
  filterCount,
  allRows,
  columnSummary,
  validFilters,
  validColumns,
  attempted,
  nameRef,
  disabled,
  reviewing = false,
}: {
  name: string;
  scope: OmsPresetScope;
  onName: (name: string) => void;
  onScope: (scope: OmsPresetScope) => void;
  filterCount: number;
  allRows: boolean;
  columnSummary: string;
  validFilters: boolean;
  validColumns: boolean;
  attempted: boolean;
  nameRef: React.RefObject<InputProps.Ref>;
  disabled: boolean;
  reviewing?: boolean;
}) {
  return (
    <SpaceBetween size="l">
      <Box color="text-body-secondary">
        {reviewing
          ? "Review and save changes to this private preset."
          : "Save settings for future reports. Presets are private to you and this product."}
      </Box>
      <div className="oms-preset-name">
        <FormField
          label="Preset name"
          constraintText="Up to 80 characters. Names must be unique for this product."
          errorText={
            attempted && !name.trim() ? "Enter a preset name." : undefined
          }
        >
          <Input
            ref={nameRef}
            autoFocus
            disabled={disabled}
            ariaLabel="Preset name"
            value={name}
            onChange={({ detail }) => onName(detail.value)}
          />
        </FormField>
      </div>
      <FormField label="Settings to save">
        <RadioGroup
          value={scope}
          items={[
            { value: "both", label: "Rows and columns", disabled },
            { value: "filters", label: "Rows only", disabled },
            { value: "columns", label: "Columns only", disabled },
          ]}
          onChange={({ detail }) => onScope(detail.value as OmsPresetScope)}
        />
      </FormField>
      <SpaceBetween size="s">
        {scope !== "columns" &&
          (validFilters ? (
            !reviewing && (
              <Box>
                {allRows
                  ? "All rows"
                  : `${filterCount} active ${
                      filterCount === 1 ? "filter" : "filters"
                    }`}
              </Box>
            )
          ) : (
            <Alert type="warning">
              Correct the active filters before saving them.
            </Alert>
          ))}
        {scope !== "filters" &&
          (validColumns ? (
            !reviewing && <Box>{columnSummary}</Box>
          ) : (
            <Alert type="warning">
              Correct the column selection before saving it.
            </Alert>
          ))}
      </SpaceBetween>
    </SpaceBetween>
  );
}

type PresetManagement = {
  state: OmsPresetsState;
  rename?: { id: string; name: string };
  setRename: React.Dispatch<
    React.SetStateAction<{ id: string; name: string } | undefined>
  >;
  deleting?: string;
  setDeleting: React.Dispatch<React.SetStateAction<string | undefined>>;
  input: React.RefObject<InputProps.Ref>;
  onDeleted: (id: string) => void;
  onRenamed: (preset: OmsPresetMetadata) => void;
  onEdit: (id: string) => void;
};

function presetNameCell(preset: OmsPresetMetadata, controls: PresetManagement) {
  const { rename, state, input, setRename } = controls;
  return rename?.id === preset.presetId ? (
    <FormField label="New name">
      <Input
        ref={input}
        autoFocus
        ariaLabel={`Rename ${preset.name}`}
        value={rename.name}
        disabled={state.change.isLoading}
        onChange={({ detail }) => setRename({ ...rename, name: detail.value })}
      />
    </FormField>
  ) : (
    <Box fontWeight="bold">{preset.name}</Box>
  );
}

function presetActionsCell(
  preset: OmsPresetMetadata,
  controls: PresetManagement
) {
  const {
    rename,
    state,
    input,
    setRename,
    deleting,
    setDeleting,
    onDeleted,
    onRenamed,
    onEdit,
  } = controls;
  if (rename?.id === preset.presetId)
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <Button
          disabled={state.change.isLoading}
          onClick={() => setRename(undefined)}
        >
          Cancel
        </Button>
        <Button
          disabled={!rename.name.trim()}
          loading={state.change.isLoading}
          loadingText="Renaming"
          onClick={() => {
            state.change
              .mutateAsync({
                action: "rename",
                existing: preset,
                name: rename.name,
              })
              .then((result) => {
                if (result.preset) onRenamed(result.preset);
                setRename(undefined);
              })
              .catch(() => input.current?.focus());
          }}
        >
          Save name
        </Button>
      </SpaceBetween>
    );
  if (deleting === preset.presetId)
    return (
      <SpaceBetween size="xs">
        <Box>Delete “{preset.name}”?</Box>
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            disabled={state.change.isLoading}
            onClick={() => setDeleting(undefined)}
          >
            Cancel
          </Button>
          <Button
            loading={state.change.isLoading}
            loadingText="Deleting"
            onClick={() => {
              state.change
                .mutateAsync({ action: "delete", existing: preset })
                .then(() => {
                  onDeleted(preset.presetId);
                  setDeleting(undefined);
                })
                .catch(() => undefined);
            }}
          >
            Delete preset
          </Button>
        </SpaceBetween>
      </SpaceBetween>
    );
  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Button
        disabled={state.change.isLoading || state.read.isLoading}
        onClick={() => onEdit(preset.presetId)}
      >
        Edit preset
      </Button>
      <Button
        iconName="edit"
        ariaLabel={`Rename preset ${preset.name}`}
        disabled={state.change.isLoading || state.read.isLoading}
        onClick={() => {
          state.change.reset();
          setDeleting(undefined);
          setRename({ id: preset.presetId, name: preset.name });
        }}
      />
      <Button
        iconName="remove"
        ariaLabel={`Delete preset ${preset.name}`}
        disabled={state.change.isLoading || state.read.isLoading}
        onClick={() => {
          state.change.reset();
          setRename(undefined);
          setDeleting(preset.presetId);
        }}
      />
    </SpaceBetween>
  );
}

export function OMSPresetManager({
  state,
  onDeleted,
  onRenamed,
  onEdit,
}: {
  state: OmsPresetsState;
  onDeleted: (id: string) => void;
  onRenamed: (preset: OmsPresetMetadata) => void;
  onEdit: (id: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rename, setRename] = useState<{ id: string; name: string }>();
  const [deleting, setDeleting] = useState<string>();
  const input = useRef<InputProps.Ref>(null);
  const searchControl = useRef<TextFilterProps.Ref>(null);
  useEffect(() => searchControl.current?.focus(), []);
  const controls = {
    state,
    rename,
    setRename,
    deleting,
    setDeleting,
    input,
    onDeleted: (id: string) => {
      onDeleted(id);
      searchControl.current?.focus();
    },
    onRenamed: (preset: OmsPresetMetadata) => {
      onRenamed(preset);
      searchControl.current?.focus();
    },
    onEdit,
  };
  const items = (state.list.data || []).filter((preset) =>
    preset.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
  );
  const pages = Math.max(1, Math.ceil(items.length / 10));
  const currentPage = Math.min(page, pages);
  let recoveryAction: React.ReactNode;
  if (
    state.change.error instanceof PresetRetryExpiredError &&
    state.change.error.reconciled
  )
    recoveryAction = (
      <Button onClick={() => state.restartExpiredRequest()}>
        Start new request
      </Button>
    );
  else if (state.read.isError)
    recoveryAction = (
      <Button
        onClick={() => {
          if (state.read.variables) onEdit(state.read.variables);
        }}
      >
        Retry preset
      </Button>
    );
  else
    recoveryAction = (
      <Button
        onClick={() => {
          state.list.refetch().catch(() => undefined);
        }}
      >
        Reload list
      </Button>
    );
  return (
    <SpaceBetween size="m">
      <Box color="text-body-secondary">
        Manage your presets for this product. Existing exports and applied
        settings are preserved.
      </Box>
      {(state.list.isError || state.change.isError || state.read.isError) && (
        <Alert type="error" header="Preset change unavailable">
          {presetErrorMessage(
            state.change.error || state.read.error || state.list.error
          )}
          {recoveryAction}
        </Alert>
      )}
      <Table<OmsPresetMetadata>
        variant="embedded"
        loading={state.list.isLoading}
        loadingText="Loading your presets"
        items={items.slice((currentPage - 1) * 10, currentPage * 10)}
        trackBy="presetId"
        filter={
          <TextFilter
            ref={searchControl}
            filteringText={search}
            filteringPlaceholder="Search preset names"
            filteringAriaLabel="Search preset names"
            filteringClearAriaLabel="Clear preset search"
            onChange={({ detail }) => {
              setSearch(detail.filteringText);
              setPage(1);
            }}
          />
        }
        pagination={
          items.length > 10 ? (
            <Pagination
              currentPageIndex={currentPage}
              pagesCount={pages}
              onChange={({ detail }) => setPage(detail.currentPageIndex)}
              ariaLabels={{
                nextPageLabel: "Next preset page",
                previousPageLabel: "Previous preset page",
                pageLabel: (index) => `Preset page ${index}`,
              }}
            />
          ) : undefined
        }
        empty={
          <Box textAlign="center" padding="l">
            {search
              ? "No matching presets."
              : "No saved presets for this product."}
          </Box>
        }
        columnDefinitions={[
          {
            id: "name",
            header: "Name",
            width: 220,
            cell: (preset) => presetNameCell(preset, controls),
          },
          {
            id: "scope",
            header: "Settings",
            cell: (preset) => presetScopeLabel[preset.scope],
          },
          {
            id: "updated",
            header: "Last edited",
            cell: (preset) => reportTimestamp(preset.updatedAt),
          },
          {
            id: "actions",
            header: "Actions",
            minWidth: 310,
            cell: (preset) => presetActionsCell(preset, controls),
          },
        ]}
      />
    </SpaceBetween>
  );
}
